import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface Image {
  id: string;
  image: string;
  title: string;
}

interface ImageGalleryModalProps {
  isOpen: boolean;
  images: Image[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({
  isOpen,
  images,
  currentIndex,
  onClose,
  onNavigate,
}) => {
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);

  const handleNext = useCallback(() => {
    setDirection('left');
    onNavigate((currentIndex + 1) % images.length);
  }, [currentIndex, images.length, onNavigate]);

  const handlePrevious = useCallback(() => {
    setDirection('right');
    onNavigate((currentIndex - 1 + images.length) % images.length);
  }, [currentIndex, images.length, onNavigate]);

  // Handle keyboard navigation and disable body scroll
  useEffect(() => {
    if (isOpen) {
      // Disable scrolling
      document.body.style.overflow = 'hidden';
    } else {
      // Enable scrolling
      document.body.style.overflow = '';
    }

    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, currentIndex, images.length, onClose, handleNext, handlePrevious]);

  // Handle touch swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
  };

  const handleSwipe = () => {
    const swipeThreshold = 50;
    const difference = touchStartX.current - touchEndX.current;

    if (Math.abs(difference) > swipeThreshold) {
      if (difference > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
    }
  };

  const slideVariants = {
    enter: (dir: 'left' | 'right' | null) => ({
      x: dir === 'left' ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (dir: 'left' | 'right' | null) => ({
      zIndex: 0,
      x: dir === 'left' ? -1000 : 1000,
      opacity: 0,
    }),
  };

  const currentImage = images[currentIndex];
  if (!currentImage) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4 sm:p-6 md:p-8"
            onClick={onClose}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Image Container - Click to stop propagation */}
            <motion.div
              onClick={(e) => e.stopPropagation()}
              className="relative w-full h-full flex items-center justify-center rounded-lg sm:rounded-xl overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              {/* Image */}
              <AnimatePresence mode="wait" custom={direction}>
                <motion.img
                  key={currentIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: 'spring', stiffness: 400, damping: 40 },
                    opacity: { duration: 0.2 },
                  }}
                  src={currentImage.image}
                  alt={currentImage.title}
                  className="max-w-full max-h-full w-auto h-auto object-contain drop-shadow-2xl"
                  loading="lazy"
                  decoding="async"
                />
              </AnimatePresence>

            </motion.div>

            {/* Navigation Controls */}
            {images.length > 1 && (
              <>
                {/* Previous Button - Left (Desktop & Tablet only) */}
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevious();
                  }}
                  whileHover={{
                    backgroundColor: 'rgba(210, 100%, 55%, 0.3)',
                    boxShadow: '0 0 20px rgba(210, 100%, 55%, 0.2)'
                  }}
                  whileTap={{ opacity: 0.8 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className="hidden sm:flex absolute left-6 sm:left-8 lg:left-10 top-1/2 -translate-y-1/2 z-[60] h-12 w-12 lg:h-14 lg:w-14 bg-gradient-to-br from-primary/30 to-primary/10 backdrop-blur-xl text-white rounded-full items-center justify-center border border-primary/40 hover:border-primary/60 transition-all duration-200"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6 lg:w-7 lg:h-7" />
                </motion.button>

                {/* Next Button - Right (Desktop & Tablet only) */}
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNext();
                  }}
                  whileHover={{
                    backgroundColor: 'rgba(210, 100%, 55%, 0.3)',
                    boxShadow: '0 0 20px rgba(210, 100%, 55%, 0.2)'
                  }}
                  whileTap={{ opacity: 0.8 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className="hidden sm:flex absolute right-6 sm:right-8 lg:right-10 top-1/2 -translate-y-1/2 z-[60] h-12 w-12 lg:h-14 lg:w-14 bg-gradient-to-br from-primary/30 to-primary/10 backdrop-blur-xl text-white rounded-full items-center justify-center border border-primary/40 hover:border-primary/60 transition-all duration-200"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6 lg:w-7 lg:h-7" />
                </motion.button>

                {/* Mobile Swipe Hint */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="absolute top-8 left-1/2 -translate-x-1/2 sm:hidden text-white/50 text-xs font-medium"
                >
                  ← Swipe →
                </motion.div>
              </>
            )}

            {/* Close Button - Top Right */}
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="absolute top-4 sm:top-6 md:top-8 right-4 sm:right-6 md:right-8 z-[60] bg-white/20 backdrop-blur-lg text-white rounded-full p-3 sm:p-4"
              aria-label="Close gallery"
            >
              <X className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />
            </motion.button>

            {/* Keyboard Hint - Desktop Only */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="absolute top-8 left-1/2 -translate-x-1/2 hidden sm:block text-white/40 text-xs font-medium tracking-wide"
            >
              ← Arrows → | ESC to close
            </motion.div>

          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
